import { useEffect, useRef, useState } from 'react';
import { Heading, Text, Badge, DiffEntry } from '@bq/web-components';
import styles from './AgenticDiffSection.module.css';

interface DiffItem {
  changeType: 'added' | 'modified' | 'removed';
  crdId: string;
  layer: string;
  description: string;
  composes?: string[];
}

const SAMPLE_DIFF: DiffItem[] = [
  {
    changeType: 'added',
    crdId: 'r3.bq-search-bar',
    layer: 'r3',
    description: 'New search bar component with autocomplete and keyboard navigation.',
    composes: ['r2.bq-text', 'r2.bq-icon', 'r2.bq-button'],
  },
  {
    changeType: 'modified',
    crdId: 'r4.bq-hero-section',
    layer: 'r4',
    description: 'Updated hero section to include the new search bar below the headline.',
    composes: ['r3.bq-search-bar', 'r2.bq-heading', 'r2.bq-image'],
  },
  {
    changeType: 'added',
    crdId: 'r2.bq-avatar',
    layer: 'r2',
    description: 'Circular image element with fallback initials and status indicator.',
    composes: ['r1.colors', 'r1.borders'],
  },
  {
    changeType: 'modified',
    crdId: 'r3.bq-navbar',
    layer: 'r3',
    description: 'Added user avatar to the right side of the navigation bar.',
    composes: ['r2.bq-avatar', 'r2.bq-button', 'r2.bq-text'],
  },
  {
    changeType: 'removed',
    crdId: 'r3.bq-legacy-banner',
    layer: 'r3',
    description: 'Deprecated promotional banner — replaced by hero section updates.',
  },
  {
    changeType: 'added',
    crdId: 'r1.semantic-colors',
    layer: 'r1',
    description: 'New semantic color tokens for info, success, warning, and danger states.',
  },
];

const LAYER_ORDER = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'];

export function AgenticDiffSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Group diffs by layer for the tree
  const grouped = LAYER_ORDER.map((layer) => ({
    layer,
    items: SAMPLE_DIFF.filter((d) => d.layer === layer),
  })).filter((g) => g.items.length > 0);

  return (
    <section
      id="diff"
      ref={sectionRef}
      data-crd-id="r4.bq-agentic-diff-section"
      data-crd-layer="r4"
      className={styles.section}
    >
      <div className={styles.intro}>
        <Badge variant="default">Feature #4</Badge>
        <Heading level={2} as={2} align="center">
          Agentic Diff
        </Heading>
        <Text size="r1" align="center" color="n500">
          See how changes propagate through CRD layers. Each modification
          shows which layers it touches and what it composes.
        </Text>
      </div>

      <div className={styles.workspace}>
        {/* Diff entries */}
        <div className={styles.entries}>
          {visible &&
            SAMPLE_DIFF.map((item, i) => (
              <DiffEntry
                key={item.crdId}
                changeType={item.changeType}
                crdId={item.crdId}
                description={item.description}
                composes={item.composes}
                index={i}
              />
            ))}
        </div>

        {/* Layer tree */}
        <div className={styles.tree}>
          <Text size="r3" weight="bold" color="n500">
            Layer Tree
          </Text>
          {grouped.map((group) => (
            <div key={group.layer} className={styles.treeGroup}>
              <div className={styles.treeLayer}>
                <span className={styles.treeDot} data-layer={group.layer} />
                <Text size="r4" weight="bold" as="span">
                  {group.layer.toUpperCase()}
                </Text>
              </div>
              {group.items.map((item) => (
                <div key={item.crdId} className={styles.treeItem}>
                  <span className={styles.treeLine} />
                  <code className={styles.treeId}>{item.crdId}</code>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
